import PropTypes from 'prop-types'
import { sliceStatusPropTypes } from '../slice.types'

/**
 * @typedef {Object} IntegrationCategory
 * @property {number} id - Category ID
 * @property {string} name - Category name
 */

/**
 * @typedef {Object} Integration
 * @property {number} id - Integration ID
 * @property {string} name - Integration name
 * @property {string} system_name - Integration system name
 */

/**
 * @typedef {Object} AgencyIntegration
 * @property {number} id - Agency integration ID
 * @property {number} integration_id - Reference to integration
 * @property {string} name - Integration name
 * @property {string} system_name - Integration system name
 * @property {string} category - Integration category
 * @property {boolean} is_active - Active status
 * @property {Object} properties - Integration properties/configuration
 * @property {IntegrationCategory[]} integration_categories - Categories
 * @property {Integration} integration - Integration details
 */

/**
 * @typedef {Object} ClientAgencyIntegration
 * @property {number} id - Client agency integration ID
 * @property {string} name - Integration name
 * @property {string} category - Integration category
 * @property {boolean} is_active - Active status
 * @property {number} agency_integration_id - Reference to agency integration
 */

/**
 * @typedef {Object} PortalAgencyIntegration
 * @property {number} id - Portal agency integration ID
 * @property {string} name - Integration name
 * @property {string} category - Integration category
 * @property {boolean} is_active - Active status
 * @property {number} agency_integration_id - Reference to agency integration
 */

/**
 * @typedef {Object} IntegrationsData
 * @property {AgencyIntegration[]} agency_integrations - Agency-level integrations
 * @property {ClientAgencyIntegration[]} client_agency_integrations - Client-level integrations
 * @property {PortalAgencyIntegration[]} portal_agency_integrations - Portal-level integrations
 */

/**
 * @typedef {Object} IntegrationsState
 * @property {IntegrationsData|null} data - Integration data from API
 */

/** @typedef {import('../slice.types').BaseSlice & IntegrationsState} IntegrationsSlice */

/** @type {IntegrationsSlice} */
export const initialIntegrationsState = {
  status: 'init',
  errors: [],
  data: null
}

/** @enum {string} */
export const INTEGRATIONS_ACTIONS = {
  SET_INTEGRATIONS_STATUS: 'SET_INTEGRATIONS_STATUS',
  SET_INTEGRATIONS_ERRORS: 'SET_INTEGRATIONS_ERRORS',
  SET_INTEGRATIONS_DATA: 'SET_INTEGRATIONS_DATA',

  CLEAR_INTEGRATIONS_ERRORS: 'CLEAR_INTEGRATIONS_ERRORS',
  CLEAR_INTEGRATIONS_DATA: 'CLEAR_INTEGRATIONS_DATA'
}

export const integrationCategoryPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired
})

export const integrationPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  system_name: PropTypes.string.isRequired
})

export const agencyIntegrationPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  integration_id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  system_name: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  is_active: PropTypes.bool.isRequired,
  properties: PropTypes.object,
  integration_categories: PropTypes.arrayOf(integrationCategoryPropType),
  integration: integrationPropType
})

export const clientAgencyIntegrationPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  is_active: PropTypes.bool.isRequired,
  agency_integration_id: PropTypes.number.isRequired
})

export const portalAgencyIntegrationPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  is_active: PropTypes.bool.isRequired,
  agency_integration_id: PropTypes.number.isRequired
})

export const integrationsDataPropType = PropTypes.shape({
  agency_integrations: PropTypes.arrayOf(agencyIntegrationPropType),
  client_agency_integrations: PropTypes.arrayOf(clientAgencyIntegrationPropType),
  portal_agency_integrations: PropTypes.arrayOf(portalAgencyIntegrationPropType)
})

export const integrationsSlicePropType = PropTypes.shape({
  status: sliceStatusPropTypes,
  errors: PropTypes.arrayOf(PropTypes.shape({ message: PropTypes.string })),
  data: integrationsDataPropType
})

/**
 * Base slice actions
 * @typedef {{ type: INTEGRATIONS_ACTIONS, payload: IntegrationsSlice['status'] }} IntegrationsStatusAction
 * @typedef {{ type: INTEGRATIONS_ACTIONS, payload: IntegrationsSlice['errors'] }} IntegrationsErrorsAction
 *
 * Integrations slice actions
 * @typedef {{ type: INTEGRATIONS_ACTIONS, payload: IntegrationsSlice['data'] }} IntegrationsDataAction
 *
 * Clear action
 * @typedef {{ type: INTEGRATIONS_ACTIONS }} IntegrationsClearAction
 *
 * @typedef {IntegrationsStatusAction | IntegrationsErrorsAction | IntegrationsDataAction | IntegrationsClearAction} IntegrationsAction
 * */
